import React, { useState, useEffect, useReducer, useCallback, useContext, useRef } from "react";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import SearchIcon from "@material-ui/icons/Search";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import Tooltip from "@material-ui/core/Tooltip";
import Chip from "@material-ui/core/Chip";
import Divider from "@material-ui/core/Divider";
import CircularProgress from "@material-ui/core/CircularProgress";

import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import RoomServiceIcon from "@material-ui/icons/RoomService";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import LanguageIcon from "@material-ui/icons/Language";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";

import api from "../../services/api";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ServiceModal from "../../components/ServiceModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import { socketConnection } from "../../services/socket";
import { AuthContext } from "../../context/Auth/AuthContext";

const reducer = (state, action) => {
  if (action.type === "LOAD_SERVICES") {
    const services = action.payload;
    const newServices = [];
    services.forEach((service) => {
      const idx = state.findIndex((s) => s.id === service.id);
      if (idx !== -1) {
        state[idx] = service;
      } else {
        newServices.push(service);
      }
    });
    return [...state, ...newServices];
  }
  if (action.type === "UPDATE_SERVICE") {
    const service = action.payload;
    const idx = state.findIndex((s) => s.id === service.id);
    if (idx !== -1) {
      state[idx] = service;
      return [...state];
    }
    return [service, ...state];
  }
  if (action.type === "DELETE_SERVICE") {
    const serviceId = action.payload;
    const idx = state.findIndex((s) => s.id === serviceId);
    if (idx !== -1) state.splice(idx, 1);
    return [...state];
  }
  if (action.type === "RESET") return [];
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  brandPaper: {
    padding: theme.spacing(3),
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  tabBar: {
    marginBottom: theme.spacing(1),
  },
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(6, 2),
    color: theme.palette.text.disabled,
  },
  emptyIcon: {
    fontSize: 56,
    marginBottom: theme.spacing(1.5),
    opacity: 0.35,
  },
  sectionDivider: {
    margin: theme.spacing(2.5, 0),
  },
}));

const formatPrice = (value) =>
  Number(value || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const PRESET_COLORS = [
  "#2563eb", "#1e3a8a", "#0891b2", "#16a34a",
  "#65a30d", "#ca8a04", "#ea580c", "#dc2626",
  "#db2777", "#7c3aed", "#4f46e5", "#0f172a",
];

const HEX_RE = /^#[0-9a-fA-F]{6}$/;

const Services = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);

  const [tab, setTab] = useState(0);

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [deletingService, setDeletingService] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [services, dispatch] = useReducer(reducer, []);
  const [serviceModalOpen, setServiceModalOpen] = useState(false);

  const [slug, setSlug] = useState("");
  const [savedSlug, setSavedSlug] = useState("");
  const [savingSlug, setSavingSlug] = useState(false);
  const [brandColors, setBrandColors] = useState([]);
  const [customColor, setCustomColor] = useState("");
  const [savingBrand, setSavingBrand] = useState(false);
  const [logo, setLogo] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInputRef = useRef(null);

  useEffect(() => {
    const fetchSlug = async () => {
      try {
        const { data } = await api.get(`/companies/${user.companyId}/slug`);
        setSlug(data.slug || "");
        setSavedSlug(data.slug || "");
        setBrandColors(data.brandColors || []);
        setLogo(data.logo || null);
      } catch (err) {
        // silencioso: usuário não-admin pode não ter acesso
      }
    };
    if (user?.companyId) fetchSlug();
  }, [user]);

  const handleLogoSelected = async (e) => {
    const file = e.target.files?.[0];
    const inputEl = e.target; // captura antes do primeiro await — React 16 anula e.target após o yield
    if (!file) return;
    const allowed = ["image/png", "image/jpeg", "image/jpg", "image/webp", "image/svg+xml"];
    if (!allowed.includes(file.type)) {
      toast.error("Formato inválido. Use PNG, JPG, WEBP ou SVG.");
      inputEl.value = "";
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Imagem muito grande. Máximo de 2 MB.");
      inputEl.value = "";
      return;
    }
    const formData = new FormData();
    formData.append("file", file);
    setUploadingLogo(true);
    try {
      const { data } = await api.post(`/companies/${user.companyId}/logo`, formData);
      setLogo(data.logo);
      toast.success("Logo enviada.");
    } catch (err) {
      toastError(err);
    } finally {
      setUploadingLogo(false);
      inputEl.value = "";
    }
  };

  const handleRemoveLogo = async () => {
    setUploadingLogo(true);
    try {
      await api.delete(`/companies/${user.companyId}/logo`);
      setLogo(null);
      toast.success("Logo removida.");
    } catch (err) {
      toastError(err);
    }
    setUploadingLogo(false);
  };

  const logoUrl = logo ? `${process.env.REACT_APP_BACKEND_URL}/public/${logo}` : "";

  const toggleBrandColor = (color) => {
    setBrandColors((prev) => {
      if (prev.includes(color)) return prev.filter((c) => c !== color);
      if (prev.length >= 3) return prev;
      return [...prev, color];
    });
  };

  const addCustomColor = () => {
    const c = customColor.trim().toLowerCase();
    if (!HEX_RE.test(c)) { toast.error("Cor inválida. Use o formato #RRGGBB."); return; }
    if (brandColors.includes(c)) { setCustomColor(""); return; }
    if (brandColors.length >= 3) { toast.error("Máximo de 3 cores."); return; }
    setBrandColors([...brandColors, c]);
    setCustomColor("");
  };

  const handleSaveBrand = async () => {
    setSavingBrand(true);
    try {
      const { data } = await api.put(`/companies/${user.companyId}/brand-colors`, { brandColors });
      setBrandColors(data.brandColors || []);
      toast.success("Cores da marca salvas.");
    } catch (err) {
      toastError(err);
    }
    setSavingBrand(false);
  };

  const handleSaveSlug = async () => {
    setSavingSlug(true);
    try {
      const { data } = await api.put(`/companies/${user.companyId}/slug`, { slug });
      setSlug(data.slug);
      setSavedSlug(data.slug);
      toast.success("Link de agendamento salvo.");
    } catch (err) {
      toastError(err);
    }
    setSavingSlug(false);
  };

  const publicUrl = savedSlug ? `${window.location.origin}/agenda/${savedSlug}` : "";

  const handleCopyUrl = () => {
    if (!publicUrl) return;
    void navigator.clipboard?.writeText(publicUrl);
    toast.success("Link copiado!");
  };

  const fetchServices = useCallback(async () => {
    try {
      const { data } = await api.get("/services", { params: { searchParam, pageNumber } });
      dispatch({ type: "LOAD_SERVICES", payload: data.services });
      setHasMore(data.hasMore);
      setLoading(false);
    } catch (err) {
      toastError(err);
    }
  }, [searchParam, pageNumber]);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delay = setTimeout(() => { fetchServices(); }, 500);
    return () => clearTimeout(delay);
  }, [searchParam, pageNumber, fetchServices]);

  useEffect(() => {
    const socket = socketConnection({ companyId: user.companyId });
    socket.on("service", (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_SERVICE", payload: data.service });
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE_SERVICE", payload: +data.serviceId });
      }
    });
    return () => { socket.disconnect(); };
  }, [user]);

  const handleOpenServiceModal = () => { setSelectedService(null); setServiceModalOpen(true); };
  const handleCloseServiceModal = () => { setSelectedService(null); setServiceModalOpen(false); };
  const handleSearch = (e) => setSearchParam(e.target.value.toLowerCase());
  const handleEditService = (service) => { setSelectedService(service); setServiceModalOpen(true); };

  const handleDeleteService = async (serviceId) => {
    try {
      await api.delete(`/services/${serviceId}`);
      toast.success("Serviço excluído com sucesso.");
    } catch (err) {
      toastError(err);
    }
    setDeletingService(null);
    setSearchParam("");
    setPageNumber(1);
    dispatch({ type: "RESET" });
    setPageNumber(1);
    await fetchServices();
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      setPageNumber((prev) => prev + 1);
    }
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={deletingService && `Excluir ${deletingService.name}?`}
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => handleDeleteService(deletingService.id)}
      >
        Tem certeza que deseja excluir este serviço? Esta ação não pode ser desfeita.
      </ConfirmationModal>
      <ServiceModal
        open={serviceModalOpen}
        onClose={handleCloseServiceModal}
        reload={fetchServices}
        aria-labelledby="form-dialog-title"
        serviceId={selectedService && selectedService.id}
      />

      <MainHeader>
        <Title>Serviços</Title>
        <MainHeaderButtonsWrapper>
          {tab === 0 && (
            <>
              <TextField
                placeholder="Buscar serviço..."
                type="search"
                value={searchParam}
                onChange={handleSearch}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon style={{ color: "gray" }} />
                    </InputAdornment>
                  ),
                }}
              />
              <Button variant="contained" color="primary" onClick={handleOpenServiceModal}>
                Adicionar
              </Button>
            </>
          )}
        </MainHeaderButtonsWrapper>
      </MainHeader>

      {/* Tab bar */}
      <Paper variant="outlined" className={classes.tabBar}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab
            label={
              <Box display="flex" alignItems="center" style={{ gap: 6 }}>
                <RoomServiceIcon fontSize="small" />
                <span>Serviços</span>
              </Box>
            }
          />
          <Tab
            label={
              <Box display="flex" alignItems="center" style={{ gap: 6 }}>
                <LanguageIcon fontSize="small" />
                <span>Página Pública</span>
              </Box>
            }
          />
        </Tabs>
      </Paper>

      {/* ── Aba 0: tabela de serviços ─────────────────────────────────────── */}
      {tab === 0 && (
        <Paper className={classes.mainPaper} variant="outlined" onScroll={handleScroll}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell align="left">Nome</TableCell>
                <TableCell align="center">Duração</TableCell>
                <TableCell align="center">Preço</TableCell>
                <TableCell align="left">Profissionais</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="center">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <>
                {!loading && services.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Box className={classes.emptyState}>
                        <RoomServiceIcon className={classes.emptyIcon} />
                        <Typography variant="body2" gutterBottom>
                          {searchParam
                            ? `Nenhum serviço encontrado para "${searchParam}"`
                            : "Nenhum serviço cadastrado ainda."}
                        </Typography>
                        {!searchParam && (
                          <Button
                            size="small"
                            color="primary"
                            variant="outlined"
                            onClick={handleOpenServiceModal}
                            style={{ marginTop: 8 }}
                          >
                            Criar primeiro serviço
                          </Button>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
                {services.map((service) => (
                  <TableRow key={service.id} hover>
                    <TableCell align="left">{service.name}</TableCell>
                    <TableCell align="center">{service.durationMinutes} min</TableCell>
                    <TableCell align="center">{formatPrice(service.price)}</TableCell>
                    <TableCell align="left">
                      {(service.users || []).length > 0 ? (
                        <Box display="flex" flexWrap="wrap" style={{ gap: 4 }}>
                          {(service.users || []).map((u) => (
                            <Chip key={u.id} size="small" label={u.name} />
                          ))}
                        </Box>
                      ) : (
                        <span style={{ color: "rgba(128,128,128,0.5)" }}>—</span>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        size="small"
                        label={service.active ? "Ativo" : "Inativo"}
                        style={{
                          backgroundColor: service.active ? "#4caf50" : "#9e9e9e",
                          color: "#fff",
                          fontWeight: 600,
                        }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Editar">
                        <IconButton size="small" onClick={() => handleEditService(service)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Excluir">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setConfirmModalOpen(true);
                            setDeletingService(service);
                          }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {loading && <TableRowSkeleton columns={6} />}
              </>
            </TableBody>
          </Table>
        </Paper>
      )}

      {/* ── Aba 1: página pública ─────────────────────────────────────────── */}
      {tab === 1 && (
        <Paper variant="outlined" className={classes.brandPaper}>

          {/* Link público */}
          <Typography variant="subtitle1" style={{ fontWeight: 700, marginBottom: 4 }}>
            Link público de agendamento
          </Typography>
          <Box display="flex" alignItems="center" style={{ gap: 8, flexWrap: "wrap" }}>
            <Typography variant="body2" color="textSecondary">
              {window.location.origin}/agenda/
            </Typography>
            <TextField
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              placeholder="seu-negocio"
              size="small"
              variant="outlined"
            />
            <Button
              variant="contained"
              color="primary"
              size="small"
              disabled={savingSlug || !slug}
              onClick={handleSaveSlug}
            >
              Salvar
            </Button>
            {publicUrl && (
              <Tooltip title="Copiar link">
                <IconButton size="small" onClick={handleCopyUrl}>
                  <FileCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          {publicUrl && (
            <Typography variant="caption" color="textSecondary" style={{ marginTop: 4, display: "block" }}>
              Compartilhe:{" "}
              <a href={publicUrl} target="_blank" rel="noopener noreferrer">
                {publicUrl}
              </a>
            </Typography>
          )}

          <Divider className={classes.sectionDivider} />

          {/* Cores da marca */}
          <Typography variant="subtitle1" style={{ fontWeight: 700, marginBottom: 4 }}>
            Cores da marca <Typography variant="caption" color="textSecondary">(até 3)</Typography>
          </Typography>
          <Typography variant="caption" color="textSecondary">
            A 1ª cor é a principal (botões e destaques). O cabeçalho usa um gradiente das cores
            escolhidas. Sem seleção, usa o azul padrão.
          </Typography>

          <Box display="flex" alignItems="center" style={{ gap: 8, flexWrap: "wrap", marginTop: 10 }}>
            {brandColors.length === 0 ? (
              <Typography variant="body2" color="textSecondary">
                Nenhuma cor selecionada.
              </Typography>
            ) : (
              brandColors.map((c, i) => (
                <Chip
                  key={c}
                  label={`${i === 0 ? "Principal • " : ""}${c}`}
                  onDelete={() => toggleBrandColor(c)}
                  style={{ backgroundColor: c, color: "#fff", fontWeight: 600 }}
                />
              ))
            )}
          </Box>

          {brandColors.length > 0 && (
            <Box
              style={{
                height: 36,
                borderRadius: 8,
                marginTop: 10,
                background: `linear-gradient(135deg, ${brandColors[0]}, ${brandColors[1] || brandColors[0]})`,
              }}
            />
          )}

          <Box display="flex" style={{ gap: 8, flexWrap: "wrap", marginTop: 14 }}>
            {PRESET_COLORS.map((c) => {
              const active = brandColors.includes(c);
              return (
                <div
                  key={c}
                  onClick={() => toggleBrandColor(c)}
                  title={c}
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 7,
                    cursor: "pointer",
                    backgroundColor: c,
                    boxShadow: active ? `0 0 0 2px #fff, 0 0 0 4px ${c}` : "none",
                    border: "1px solid rgba(0,0,0,0.12)",
                  }}
                />
              );
            })}
          </Box>

          <Box display="flex" alignItems="center" style={{ gap: 8, flexWrap: "wrap", marginTop: 14 }}>
            <TextField
              value={customColor}
              onChange={(e) => setCustomColor(e.target.value)}
              placeholder="#FF5733"
              size="small"
              variant="outlined"
              style={{ width: 130 }}
            />
            <Button size="small" onClick={addCustomColor} disabled={brandColors.length >= 3}>
              Adicionar cor
            </Button>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={handleSaveBrand}
              disabled={savingBrand}
            >
              Salvar cores
            </Button>
          </Box>

          <Divider className={classes.sectionDivider} />

          {/* Logo */}
          <Typography variant="subtitle1" style={{ fontWeight: 700, marginBottom: 4 }}>
            Logo
          </Typography>
          <Typography variant="caption" color="textSecondary">
            Aparece no topo da página pública. Sem logo, usamos as iniciais da empresa.
            PNG, JPG, WEBP ou SVG, até 2 MB.
          </Typography>
          <Box display="flex" alignItems="center" style={{ gap: 14, flexWrap: "wrap", marginTop: 10 }}>
            {logoUrl ? (
              <img
                src={logoUrl}
                alt="Logo"
                style={{
                  width: 72,
                  height: 72,
                  objectFit: "contain",
                  borderRadius: 12,
                  border: "1px solid rgba(128,128,128,0.3)",
                  background: "#fff",
                  padding: 4,
                }}
              />
            ) : (
              <Box
                style={{
                  width: 72,
                  height: 72,
                  borderRadius: 12,
                  border: "1px dashed rgba(128,128,128,0.4)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "rgba(128,128,128,0.7)",
                  textAlign: "center",
                }}
              >
                <Typography variant="caption">Sem logo</Typography>
              </Box>
            )}
            <input
              ref={logoInputRef}
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              onChange={handleLogoSelected}
            />
            <Box display="flex" flexDirection="column" style={{ gap: 8 }}>
              <Button
                size="small"
                variant="outlined"
                disabled={uploadingLogo}
                onClick={() => logoInputRef.current && logoInputRef.current.click()}
                startIcon={
                  uploadingLogo ? (
                    <CircularProgress size={14} style={{ color: "inherit" }} />
                  ) : undefined
                }
              >
                {uploadingLogo ? "Enviando..." : logoUrl ? "Trocar logo" : "Enviar logo"}
              </Button>
              {logoUrl && (
                <Button
                  size="small"
                  disabled={uploadingLogo}
                  onClick={handleRemoveLogo}
                  style={{ color: "#dc2626" }}
                >
                  Remover
                </Button>
              )}
            </Box>
          </Box>
        </Paper>
      )}
    </MainContainer>
  );
};

export default Services;
